import React from 'react';

import { Icon, Skeleton, Text } from '../../../../components';
import { HOME, HUB_AREA, type HubArea } from '../../../../theme/tokens';
import { hubTarget } from '../../../../features/ftux/targets';
import { safeHex } from '../../../art-style/definition';
import type { WebsiteCardView, WebsiteCardViewBody } from '../../card-summary';
import { HUB_LINK_STATE } from '../../hub-navigation';
import {
  Arrow,
  Body,
  Chip,
  ChipSlot,
  ContextLine,
  ContextRow,
  Description,
  Details,
  Footer,
  Header,
  Heading,
  Line,
  Root,
  SkeletonLayer,
  Status,
  Swatch,
  Swatches,
  Thumb,
  Tile,
  TitleLink,
  Words,
} from './WebsiteHubCard.styles';

export type WebsiteHubCardProps = {
  /** The area the card opens (a Website or a Marketing one): its colour is the card's glow. */
  area: HubArea;
  /** The area's name, already translated. */
  title: string;
  /** The area's one-line description, already translated. */
  description: string;
  /** The tile's glyph, sized and coloured by the caller. */
  icon: React.ReactNode;
  /** The area's own page: where the card itself goes. */
  to: string;
  view: WebsiteCardView;
  /** The card's place on its hub (0 for the first): its entrance starts that many stagger steps after the first's. */
  enterIndex?: number;
  testId?: string;
};

/** The most swatches a context line shows. */
const MAX_SWATCHES = 6;

/** The palette's plain hex colours, in order, at most six: a swatch's colour is API data that reaches the stylesheet,
 *  so any other value is dropped rather than painted. */
const paintableSwatches = (swatches: readonly string[] | undefined): string[] =>
  (swatches ?? [])
    .flatMap((value) => {
      const hex = safeHex(value);
      return hex ? [hex] : [];
    })
    .slice(0, MAX_SWATCHES);

/** The thumbnail's address when it is an http(s) one (a same-origin path resolves to one), else none: the address is
 *  API data, so any other scheme is left out rather than loaded. */
const loadableThumb = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url, window.location.href);
    return protocol === 'https:' || protocol === 'http:' ? url : undefined;
  } catch {
    return undefined;
  }
};

/** The skeleton's one line: about the length of the line it stands in for. */
const SkeletonLine = (): React.ReactElement => <Skeleton height={18} width="70%" />;

/** A thumbnail, hidden until its image has decoded (or failed, which shows its tint), then faded in. Keyed by its
 *  address where it is used, so a new image starts hidden again. */
const ThumbImage = ({ src }: { src: string }): React.ReactElement => {
  const [loaded, setLoaded] = React.useState(false);
  const show = React.useCallback(() => setLoaded(true), []);
  // An image already in the cache can finish before its load listener exists: show it at once.
  const ref = React.useCallback((img: HTMLImageElement | null) => {
    if (img?.complete) setLoaded(true);
  }, []);
  return <Thumb ref={ref} src={src} alt="" $loaded={loaded} onLoad={show} onError={show} />;
};

const CardBody = ({ body }: { body: WebsiteCardViewBody }): React.ReactElement => {
  switch (body.kind) {
    case 'loading':
      return (
        <Body data-testid="hub-card-body">
          <SkeletonLine />
        </Body>
      );
    case 'failed':
      return <Body data-testid="hub-card-body" />;
    case 'waiting':
      return (
        <Body data-testid="hub-card-body">
          {body.lines.map((line) => (
            <Line key={line} data-testid="hub-card-line">
              <Text text={line} raw tag="span" />
            </Line>
          ))}
        </Body>
      );
    case 'context': {
      const swatches = paintableSwatches(body.art?.swatches);
      const thumb = loadableThumb(body.art?.thumbUrl);
      return (
        <Body data-testid="hub-card-body">
          <ContextRow $withThumb={Boolean(thumb)} $withSwatches={swatches.length > 0}>
            {thumb ? <ThumbImage key={thumb} src={thumb} /> : null}
            <ContextLine data-testid="hub-card-line">
              <Text text={body.line} raw tag="span" />
            </ContextLine>
            {swatches.length > 0 ? (
              <Swatches aria-hidden="true">
                {swatches.map((hex, index) => (
                  // A palette may repeat a colour, so the position is part of the key; the list never reorders.
                  <Swatch key={`${index}-${hex}`} $hex={hex} data-testid="hub-card-swatch" />
                ))}
              </Swatches>
            ) : null}
          </ContextRow>
        </Body>
      );
    }
    case 'get-started':
      return (
        <Body data-testid="hub-card-body">
          <Line data-testid="hub-card-line">
            <Text text={body.line} raw tag="span" />
          </Line>
        </Body>
      );
  }
};

/** Where the card's details are: still loading (the skeleton), arriving (rising in over the fading skeleton), or in. */
type StatusPhase = 'loading' | 'revealing' | 'shown';

/**
 * One area on a hub (Website or Marketing): the icon tile with, beside it, the title, description and an optional
 * status chip; along the bottom, the body (the summary of what the area holds) and, at the right, Home's arrow. The
 * whole card, the arrow included, opens the area through its stretched title link. The card shows no action buttons:
 * the view's primary and secondary actions are not rendered. The chip and the body each sit in a slot of fixed height,
 * so the card never changes height as they load: the skeleton holds the body's slot, and the details rise in over it
 * as it fades. A card whose details are in from its first render shows them
 * still. As its hub shows, the card itself fades and rises in, `enterIndex` stagger steps after the hub's first card.
 */
export const WebsiteHubCard = ({
  area,
  title,
  description,
  icon,
  to,
  view,
  enterIndex = 0,
  testId,
}: WebsiteHubCardProps): React.ReactElement => {
  const loading = view.body.kind === 'loading';
  const [phase, setPhase] = React.useState<StatusPhase>(loading ? 'loading' : 'shown');
  // Follows the view during render (React's "adjust state when a prop changes"), so the details' first frame is
  // already their reveal: the trigger is the data arriving, not the card mounting.
  if (loading && phase !== 'loading') setPhase('loading');
  else if (!loading && phase === 'loading') setPhase('revealing');
  const onRevealEnd = React.useCallback((event: React.AnimationEvent<HTMLDivElement>): void => {
    // Only the details' own rise ends the reveal, not an animation inside them.
    if (event.target === event.currentTarget) setPhase('shown');
  }, []);

  return (
    <Root
      element="article"
      glow={HUB_AREA[area].glow}
      interactive
      $enterIndex={enterIndex}
      data-area={area}
      data-body={view.body.kind}
      data-chip={view.chip?.tone ?? ''}
      // Where the FTUX finds the area's card, on the Website and the Marketing hub alike (inert outside an FTUX step).
      data-ftux={hubTarget(area)}
      data-testid={testId}
    >
      <Header>
        <Tile data-testid="hub-card-tile">
          {icon}
        </Tile>
        <Words>
          <Heading>
            <TitleLink to={to} state={HUB_LINK_STATE}>
              <Text text={title} raw tag="span" />
            </TitleLink>
          </Heading>
          <Description text={description} raw tag="paragraph" />
          <ChipSlot>
            {phase !== 'loading' && view.chip ? (
              <Details $reveal={phase === 'revealing'}>
                <Chip $tone={view.chip.tone} data-testid="hub-card-chip">
                  <Text text={view.chip.label} raw tag="span" />
                </Chip>
              </Details>
            ) : null}
          </ChipSlot>
        </Words>
      </Header>
      <Footer>
        <Status data-testid="hub-card-status">
          {phase === 'loading' ? (
            <SkeletonLayer $leaving={false}>
              <CardBody body={view.body} />
            </SkeletonLayer>
          ) : null}
          {phase === 'revealing' ? (
            <SkeletonLayer $leaving aria-hidden="true">
              <Body>
                <SkeletonLine />
              </Body>
            </SkeletonLayer>
          ) : null}
          {phase !== 'loading' ? (
            <Details $reveal={phase === 'revealing'} onAnimationEnd={onRevealEnd}>
              <CardBody body={view.body} />
            </Details>
          ) : null}
        </Status>
        <Arrow aria-hidden="true" data-testid="hub-card-arrow">
          <Icon name="arrow-right-circle" size={24} color={HOME.t50} />
        </Arrow>
      </Footer>
    </Root>
  );
};

export default WebsiteHubCard;
